import * as React from "react";
import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import DatePicker from "react-datepicker";
import { useMergeRefs } from "@chakra-ui/react";
import { CalendarInput, CalendarInputProps, MaskedCalendarInput } from "shared";
import { useDivideControlProps } from "./hooks";
import { isValid } from "date-fns";

const omittedProps = ["selected", "name", "onChange"] as const;

const getInputValue = (
  event: React.SyntheticEvent<Element> | undefined
): string | undefined => {
  const target = event?.target;
  return target instanceof HTMLInputElement ? target.value : undefined;
};

type FormCalendarInputProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> &
    Omit<CalendarInputProps, typeof omittedProps[number]> & {
      onChange?: CalendarInputProps["onChange"];
      masked?: boolean;
    };

function FormCalendarInputInner<TFieldValues extends FieldValues>(
  props: FormCalendarInputProps<TFieldValues>,
  ref?: React.ForwardedRef<DatePicker>
) {
  const { onChange: onChangeProp, disabled, masked, ...restProps } = props;
  const [calendarProps, controllerProps] = useDivideControlProps<
    TFieldValues,
    FormCalendarInputProps<TFieldValues>
  >(restProps);

  const {
    field: { ref: fieldRef, value, onChange, ...restField },
  } = useController(controllerProps);

  const calendarInputRef = useMergeRefs(ref, fieldRef);

  const handleChange: CalendarInputProps["onChange"] = (date, event) => {
    const targetValue = getInputValue(event);

    // When the user clears the input via keyboard, react-datepicker may emit a value
    // that later gets coerced into epoch (new Date(null) => 12-31-1969 for negative TZ).
    // Force `null` into RHF in that case.
    if (targetValue === "") {
      onChangeProp?.(null, event);
      onChange(null);
      return;
    }

    onChangeProp?.(date, event);
    onChange(date);
  };

  const handleInvalidInput: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    onChangeProp?.(null, event);
    onChange(null);
  };

  const date = value && isValid(new Date(value)) ? new Date(value) : null;
  const maskedMinDate = calendarProps.minDate ?? new Date(1900, 0, 1);
  const maskedMaxDate = calendarProps.maxDate ?? new Date();
  const customInput = masked ? (
    <MaskedCalendarInput
      name={controllerProps.name}
      control={controllerProps.control}
      minDate={maskedMinDate}
      maxDate={maskedMaxDate}
      onInvalidInput={handleInvalidInput}
    />
  ) : (
    calendarProps.customInput
  );

  return (
    <CalendarInput
      ref={calendarInputRef}
      {...calendarProps}
      minDate={masked ? maskedMinDate : calendarProps.minDate}
      maxDate={masked ? maskedMaxDate : calendarProps.maxDate}
      customInput={customInput}
      {...restField}
      selected={date}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

const FormCalendarInput = React.forwardRef(FormCalendarInputInner) as <
  TFieldValues extends FieldValues
>(
  props: FormCalendarInputProps<TFieldValues> & {
    ref?: React.ForwardedRef<DatePicker>;
  }
) => ReturnType<typeof FormCalendarInputInner>;

export { FormCalendarInput };
export type { FormCalendarInputProps };
